import './Tag.scss';


export namespace Tag {
    export interface ClickEventData {
        value?: string
        tagElement: HTMLDivElement
    }
}

export interface TagConfig {
    Value?: string
    HexColor?: string
    MaxTagLength?: number // FIXME IMPLEMENTAR
    IsEditable?: boolean
    ShowCloserButton?: boolean
    ForceTrim?: boolean
    Callbacks?: {
        OnKeyup?: (e: KeyboardEvent) => void
        OnRemove?: (e: MouseEvent) => void
        TagClick?: (e: CustomEvent<Tag.ClickEventData>) => void
        StartEditting?: (tagElement: HTMLDivElement, initValue: string) => void
        CancelEditting?: (tagElement: HTMLDivElement, endValue: string) => void
    }
}

type HTLMTagElement = HTMLDivElement & {
    __keyupListener?: (event: KeyboardEvent) => void
    __blurListener?: () => void
}

export class Tag {
    private config: TagConfig;
    private tagElement: HTLMTagElement;
    private textElement: HTMLSpanElement;

    constructor(config?: TagConfig) {

        this.tagElement = document.createElement('div');
        this.tagElement.className = 'tag';
        this.textElement = document.createElement('span');
        this.textElement.className = 'tag-text';
        this.tagElement.appendChild(this.textElement);
        this.tagElement.tabIndex = 0;

        this.config = config || {};

        this.init();
    }

    // *************************************************************
    // Public methods
    // *************************************************************

    public get _ControlContainer(): HTMLDivElement {
        return this.tagElement;
    }

    public set _Value(value: string) {
        this.textElement.textContent = value;
    }

    public get _Value(): string {
        return this.getTextContent();
    }

    public set _HexColor(hexColor: string) {
        this.setHexColor(hexColor);
    }

    public get _HexColor(): string | undefined {
        return this.getHexColor();
    }

    public get _IsEdditting(): boolean {
        return this.textElement.contentEditable == "true"
    }

    public _SetIsEditable(isEditable: boolean): this {
        this.config.IsEditable = isEditable;
        return this;
    }

    public _EnableEditting(): this {
        this.enableEditting();
        return this;
    }

    public _CancelEditting(): this {
        this.cancelEditting();
        return this;
    }

    public _Destroy(): this {
        this.cancelEditting();
        this.tagElement.remove();
        return this;
    }

    // *************************************************************
    // Private methods
    // *************************************************************

    private init() {
        const tagElement = this.tagElement

        this.textElement.textContent = this.config.Value || "";

        if (this.config.HexColor) {
            this.setHexColor(this.config.HexColor);
        }

        // Add click event listener
        let clickTimeout: NodeJS.Timeout | null = null;
        let nClick = 0;
        tagElement.addEventListener('click', () => {
            nClick++;
            if (clickTimeout) {
                clearTimeout(clickTimeout);
                clickTimeout = null;
            }
            if (nClick === 1) {
                clickTimeout = setTimeout(() => {
                    clickTimeout = null;
                    nClick = 0;

                    this.tagClick();
                }, 300);
            }
            else if (nClick > 1) {
                if (nClick === 2) {
                    this.doubleClick();
                }
                nClick = 0;
            }
        });
    }

    private getTextContent(): string {
        let textContent = this.textElement.textContent || "";
        if (this.config.ForceTrim) {
            textContent = textContent.trim();
        }
        return textContent;
    }

    private cancelEditting() {
        const tagElement = this.tagElement;
        const textElement = this.textElement;

        if (tagElement.__keyupListener) {
            textElement.removeEventListener('keyup', tagElement.__keyupListener);
            tagElement.__keyupListener = undefined;
        }
        if (tagElement.__blurListener) {
            textElement.removeEventListener('blur', tagElement.__blurListener);
            tagElement.__blurListener = undefined;
        }
        this.textElement.contentEditable = 'false';
        this.textElement.blur();

        const newValue = this.fixValue(this.getTextContent());
        this.textElement.textContent = newValue;

        if (this.config.Callbacks?.CancelEditting) {
            this.config.Callbacks?.CancelEditting(this.tagElement, newValue)
        }

        setTimeout(() => {
            this.showCloserButton(false);
        }, 250);
    }

    private enableEditting() {
        if (!this.config.IsEditable) {
            return;
        }
        if (this.tagElement.__keyupListener) {
            return;
        }
        // add listeners
        this.tagElement.__keyupListener = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                this.cancelEditting();
            }
            if (this.config.Callbacks?.OnKeyup) {
                this.config.Callbacks.OnKeyup(event);
            }
            // else if (this.config.Separators && this.config.Separators.includes(event.key)) this.cancelEditting()
        }
        this.tagElement.__blurListener = () => this.cancelEditting();

        this.textElement.addEventListener('keyup', this.tagElement.__keyupListener);
        this.textElement.addEventListener('blur', this.tagElement.__blurListener);

        this.showCloserButton(true);

        // FIXME mover el cursor al final del contenido editable
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(this.textElement);
        range.collapse(false); // coloca el cursor al final :p
        if (sel) {
            sel.removeAllRanges();
            sel.addRange(range);
        }
        // enable editing
        this.textElement.contentEditable = 'true';
        this.textElement.focus();

        if (this.config.Callbacks?.StartEditting) {
            this.config.Callbacks?.StartEditting(this.tagElement, this.getTextContent())
        }
    }

    private showCloserButton(show: boolean) {
        if (show) {
            if (!this.tagElement.querySelector('.tag_closer')) {
                const closerButton = document.createElement('div');
                closerButton.className = 'tag_closer';
                closerButton.innerHTML = '<div class="tag_closer_equis">×</div>';
                closerButton.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.tagElement.remove();
                    if (this.config.Callbacks?.OnRemove) {
                        this.config.Callbacks.OnRemove(e);
                    }
                });
                this.tagElement.appendChild(closerButton);
            }
        } else {
            const closerButton = this.tagElement.querySelector('.tag_closer');
            if (closerButton) {
                closerButton.remove();
            }
        }
    }

    private doubleClick() {
        // console.warn("Tag dbl-clicked:", this.textElement.textContent);
        this.enableEditting();
    }

    private tagClick() {
        // console.warn("Tag clicked:", this.tagElement.textContent);
        if (this.config.Callbacks?.TagClick) {
            const event = new CustomEvent<Tag.ClickEventData>('tag-click', {
                detail: {
                    value: this.getTextContent(),
                    tagElement: this.tagElement,
                }
            });
            this.config.Callbacks.TagClick(event);
        }
    }

    private fixValue(value: string): string {
        if (this.config.MaxTagLength && value.length > this.config.MaxTagLength) {
            return value.substring(0, this.config.MaxTagLength);
        }
        return value;
    }

    // *********************************************************
    // Color functions
    // *********************************************************

    private getHexColor(): string | undefined {
        return this.tagElement.style.getPropertyValue("--tag-color-bg")
    }

    private setHexColor(hexColor: string) {
        if (hexColor && !/^#([0-9A-F]{3}){1,2}$/i.test(hexColor)) {
            console.warn(Error('Invalid hex color format. Use #RRGGBB or #RGB.'));
        }
        if (hexColor) {
            const hexBorderColor = this.adjustHexBrightness(hexColor, -20);
            const hexEdittingBgColor = this.adjustHexBrightness(hexColor, 12);

            this.tagElement.style.setProperty("--tag-color-bg", hexColor);
            this.tagElement.style.setProperty("--tag-color-border", hexBorderColor);
            this.tagElement.style.setProperty("--tag-color-editting-bg", hexEdittingBgColor);
        } else {
            this.tagElement.style.removeProperty("--tag-color-bg");
            this.tagElement.style.removeProperty("--tag-color-border");
            this.tagElement.style.removeProperty("--tag-color-editting-bg");
        }
    }


    /**
     * Ajusta el brillo de un color hexadecimal.
     * @param hexColor Color en formato #RRGGBB o #RGB
     * @param percent Porcentaje a ajustar (-100 a 100). Negativo oscurece, positivo aclara.
     * @returns Nuevo color hexadecimal ajustado.
     */
    private adjustHexBrightness(hexColor: string, percent: number): string {
        if (hexColor && !/^#([0-9A-F]{3}){1,2}$/i.test(hexColor)) {
            console.warn(Error('Invalid hex color format. Use #RRGGBB or #RGB.'));
            return hexColor;
        }
        let color = hexColor.replace('#', '');
        if (color.length === 3) {
            color = color.split('').map(c => c + c).join('');
        }
        let num = parseInt(color, 16);
        let r = (num >> 16) & 0xFF;
        let g = (num >> 8) & 0xFF;
        let b = num & 0xFF;

        const adjust = (c: number) => {
            const amt = Math.round((percent / 100) * 255);
            return Math.max(0, Math.min(255, c + amt));
        };

        r = adjust(r);
        g = adjust(g);
        b = adjust(b);

        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }
}